/**
 * @format
 * @module lib/identity/api
 */
import cookies from "js-cookie";
import isNil from "lodash/isNil";
import once from "lodash/once";
import tgamConfig from "config/tgam";
import { ENTITLEMENTS, hasEntitlement, hasDemand } from "lib/identity/entitlements";
import { API_TIMEOUT, API_ANALYTICS, API_PROFILE, API_PUBLIC_SSO } from "lib/gamservices";
import CONSTS from "lib/consts";
import { getProp, isJest } from "utils/utils";
import { fetchTimeout, validStatus, handleJSON } from "utils/fetch";

const debugTag = "[SESSION-COOKIE]";
// This is only to display the log once, since isLoggedIn is called many times
const sessionLog = once(sessionEnv => {
  if (!isJest) {
    // eslint-disable-next-line no-console
    console.log(`${debugTag} 🍪 You have a SESSION_ENV cookie for ${sessionEnv}`);
    // eslint-disable-next-line no-console
    console.log(`To login for this environment, visit ${CONSTS.URLS.LOGOUT}`);
  }
});

export const COOKIE_DOMAIN = ".theglobeandmail.com";

let userPromise = null; // shared promise for getUserProfile()

/**
 * @description Possible roles of a TGAM user
 * @type {Object}
 * @property {{ id: Number, name: String }} anonymous
 * @property {{ id: Number, name: String }} registered
 * @property {{ id: Number, name: String }} subscribed
 */
export const roles = {
  anonymous: {
    id: 0,
    name: "Anonymous"
  },
  registered: {
    id: 1,
    name: "Registered"
  },
  subscribed: {
    id: 2,
    name: "Subscribed"
  }
};

/**
 * @description Possible user notification items
 * @enum {String}
 */
export const notifications = {
  suspension: "SUSPENSION",
  abuser: "gu_abuser_info"
};

/**
 * @method isLoggedIn
 * @description Whether the user is currently logged in.
 * @returns {Boolean}
 */
export function isLoggedIn() {
  // Environment names on the left, expected SESSION_ENV cookie values on the right.
  const sessionEnvStore = {
    production: "prod",
    staging: "prod",
    development: "stg",
    local: "stg"
  };
  /**
   * Default `currentEnv` to `production` below so that we don't have
   * to change tests that rely on the previous `isLoggedIn()` functionality
   * (ie. the boolean result of whether the SESSION cookie exists).
   */
  const currentEnv = getProp(tgamConfig, "env.name", "production").toLowerCase();
  const hasSession = cookies.get("SESSION") ? true : false;
  const sessionEnv = cookies.get("SESSION_ENV");
  const hasSessionEnv = !isNil(sessionEnv);

  if (!hasSession) {
    return false;
  }
  // NOTE: currentEnv that is set to have sessionEnvStore = "prod"
  if ((currentEnv === "production" || currentEnv === "staging") && !hasSessionEnv) {
    /**
     * A user who may or may not be internal, but they are logged in
     * on production and have an existing session. This is the only environment
     * where we should keep that session alive.
     */
    return true;
  }
  /**
   * Determine if the SESSION_ENV cookie value is correct for the environment we're in.
   * This will handle the following cases:
   * a) !prod env and the SESSION_ENV cookie is `undefined` or `null`. In a
   *  non-prod env we always want the SESSION_ENV cookie to exist.
   * b) any environment, including prod, where the SESSION_ENV cookie is defined
   *  and matches/doesn't match what we expect it to be.
   */
  const amILoggedIn = !hasSessionEnv
    ? false
    : sessionEnv.toLowerCase() === sessionEnvStore[currentEnv];
  if (currentEnv !== "production" && !amILoggedIn) {
    sessionLog(sessionEnv);
  }
  return amILoggedIn;
}

/**
 * @method isAnonymous
 * @description whether the user is anonymous
 * @returns {Boolean}
 */
export function isAnonymous() {
  return userHasRole(roles.anonymous.id);
}

/**
 * @method isRegistered
 * @description whether the user is registered
 * @returns {Boolean}
 */
export function isRegistered() {
  return userHasRole(roles.registered.id);
}

/**
 * @method isSubscribed
 * @description whether the user is a subscriber
 * @returns {Boolean}
 */
export function isSubscribed() {
  return userHasRole(roles.subscribed.id);
}

/**
 * @method isGlobeAdvisor
 * @description whether the user has the Globe Advisor entitlement
 * @returns {Boolean}
 */
export function isGlobeAdvisor() {
  return hasEntitlement(ENTITLEMENTS.ADVISOR);
}

/**
 * @method isGlobePrintSubscriber
 * @description whether the user is Globe and Mail subscriber
 * @returns {Boolean}
 */
export function isGlobePrintSubscriber() {
  return hasEntitlement(ENTITLEMENTS.GLOBEPRINT);
}

/**
 * @method isNewYorkTimesPrintSubscriber
 * @description whether the user is New York Times subscriber
 * @returns {Boolean}
 */
export function isNewYorkTimesPrintSubscriber() {
  return hasEntitlement(ENTITLEMENTS.NYTPRINT);
}

/**
 * @method isFinancialToolsTrial
 * @description whether the user is has financial tools trial entitlement.
 * ARC-7292: As a user on a fin-tools 6-week trial I have access to Subscriber-only PDFs and Portfolio.
 * @returns {Boolean}
 */
export function isFinancialToolsTrial() {
  return hasEntitlement(ENTITLEMENTS.FINTOOLSTRIAL);
}

/**
 * @method isOnDemand
 * @description Whether the user has been granted special privileges.
 * @param {"ipring" | "enterprise"} [subtype] - check for optional subtype
 * @returns {Boolean}
 */
export function isOnDemand(subtype) {
  const entitled = hasEntitlement(ENTITLEMENTS.ONDEMAND);
  const demanded = subtype ? hasDemand(subtype) : true;
  return entitled && demanded;
}

/**
 * @method userHasRole
 * @description whether the user has the specified role
 * @param {String} roleId - the role ID to check for
 * @returns {Boolean}
 */
export function userHasRole(roleId) {
  const role = getUserRole();
  return role.id === roleId;
}

/**
 * @method getUserRole
 * @description Get the user role (id and name) from the data layer
 * @returns {Object}
 */
export function getUserRole() {
  return getProp(tgamConfig, "datalayer.identity.role", {});
}

/**
 * @method getUserRoleFromServiceString
 * @description Determine the authenticated user's role based on the value of the service string
 * @param {String} [serviceString=""] - the service string value ("ssvcs") that was returned by the API
 * @returns {Object} the user's role (id and name)
 */
export function getUserRoleFromServiceString(serviceString = "") {
  // How many occurrences of "premium~" can be found in the service string?
  const ss = serviceString.toLowerCase();
  const occurrences = ss.split(",").filter(item => {
    return item.includes("premium~");
  }).length;

  if (occurrences === 0 || (occurrences === 1 && ss.indexOf("premium~ondemand") !== -1)) {
    // There are zero occurrences of "premium~" or
    // there's a single occurrence and it's "premium~ondemand"
    return roles.registered;
  }
  // There are multiple occurrences of "premium~" or
  // there's a single occurrence and it's not "premium~ondemand"
  return roles.subscribed;
}

/**
 * @method getCampaignCodeFromServiceString
 * @description Determine the campaign code based on the value of the service string
 * @returns {Object} the user's campaign code (currently the 6th and final value in ssvcs)
 */
export function getCampaignCodeFromServiceString() {
  const ss = getProp(tgamConfig, "datalayer.identity.ssvcs", null);
  const campaignCode = isNil(ss) ? "" : ss.toLowerCase();
  return campaignCode.split("~").pop();
}

/**
 * @method decodeCookieValue
 * @description The new CPC stores the | character, whereas the old one sets encoded %7C
 * @param {String} pipe - Unencoded delimiter character
 * @param {String} cookie - Cookie value with multiple (possibly encoded) delimited values
 * @returns {Array<String>} of the cookie values
 */
export function decodeCookieValue(pipe, cookie) {
  const pipeAlt = encodeURI(pipe);
  if (cookie.indexOf(pipeAlt) > 0) {
    return cookie.split(pipeAlt);
  } else {
    return cookie.split(pipe);
  }
}

/**
 * @method deleteUserCookies
 * @description Deletes all user cookies
 * @param {Object} options
 * @param {String} options.path - representing the URL associated with the cookie.
 * @param {String} options.domain - representing the first-party domain with which the cookie to remove is associated.
 */
export function deleteUserCookies(options = { path: "/", domain: COOKIE_DOMAIN }) {
  // "hd" cookie is deprecated, but delete it anyway.
  ["SSO_COOKIE", "PRODUCT_COOKIE", "SESSION", "SESSION_ENV", "hd"].forEach(nomnom => {
    cookies.remove(nomnom, options);
  });
}

/**
 * @method getAnalyticsSession
 * @description fetches user data (session) from the analytics API endpoint
 * @returns {Promise}
 * @example
 * {
 *   country:'CA',
 *   guid:'752fb81dc6e1df51a46f578b3bf76d4f',
 *   postal:'M5A 0N1',
 *   birthYear:'0',
 *   gender: '',
 *   industry: '',
 *   position: '',
 *   incomeRange: '',
 *   newsletter: '',
 *   ssvcs: 'PREMIUM~PTRIAL~P~~BASIC,PREMIUM~PAYWALL~P~~BASIC',
 *   watchlist:'n',
 *   prizm:'69'
 * }
 */
export function getAnalyticsSession() {
  return fetch(API_ANALYTICS, {
    credentials: "include"
  }).then(res => {
    if (!res.ok) {
      throw res;
    }
    return res.json();
  });
}

/**
 * @method getUserProfile
 * @description fetches the user profile from the profile API endpoint
 * @returns {Promise}
 */
export function getUserProfile() {
  // if the user is not logged in anymore, then invalidate the cached promise
  if (!isLoggedIn()) {
    userPromise = null;
  }
  // all secondary requests will get the cached promise
  if (userPromise !== null) {
    return userPromise;
  }
  const config = {
    method: "GET",
    credentials: "include"
  };

  // first request will invoke the request and cache the promise
  // This is a very high timeout due to BrowserStack and IE 11
  return (userPromise = fetchTimeout(API_TIMEOUT * 10, fetch(API_PROFILE, config))
    .then(validStatus)
    .then(handleJSON));
}

/**
 * @method generateJwtToken
 * @description Generate a JWT token for a logged in user.
 * @returns {Promise<String|null>} of token
 */
export function generateJwtToken() {
  const secret = tgamConfig.env.isProdOrStage
    ? "b1466910-0dba-11e8-842a-cbdd30d89fad"
    : "5706ed05-ee12-41f7-9f70-230670e2af94";

  const config = {
    method: "GET",
    credentials: "include",
    headers: {
      "X-GAM-CLIENT-APP-ID": "7316",
      "X-GAM-CLIENT-APP-SECRET": secret
    }
  };

  return fetchTimeout(API_TIMEOUT, fetch(API_PUBLIC_SSO, config))
    .then(validStatus)
    .then(handleJSON)
    .then(result => {
      return result.token ? result.token : null;
    });
}

/**
 * @method getSophi3Id
 * @description Retrieves the user's sophi 3 id. When we consolidate the.
 * @returns {String|null} The id or null if it cannot be retrieved
 */
export function getSophi3Id() {
  let id = null;
  const c = cookies.get("sophiTrackerid.a05e");
  const s = window.sophi && window.sophi.functions && window.sophi.functions.getDomainUserID();
  if (!isNil(c) && c.length) {
    id = c.split(".")[0];
  } else if (!isNil(s) && s !== false) {
    id = s;
  }
  return id;
}

/**
 * @method setAfterLoginCookie
 * @description Set the URL that the user will be redirected to after a successful login.
 * @param {String} value - href/url
 */
export function setAfterLoginCookie(value = window.location.href) {
  cookies.set("afterLoginGoTo", value, {
    domain: COOKIE_DOMAIN,
    path: "/"
  });
}

/**
 * @method getAfterLoginCookie
 * @description Get the cookie value redirection url.
 * @returns {Object} of cookie
 */
export function getAfterLoginCookie() {
  return cookies.get("afterLoginGoTo");
}

/**
 * @method getUserHashId
 * @deprecated Don't use this method! Please use something like this instead.
 * @example const userHashId = get(tgamConfig, "datalayer.identity.guid", null);
 * @description Returns the hashed user ID from a cookie set in GTM, or null
 * The value of the cookie changes when you log in, but it does not change
 * when you log out (i.e. it persists until the next login)
 * See: https://confluence.theglobeandmail.com/display/HOME/User+IDs
 * @returns {String|null} the hashed user id or null
 */
export function getUserHashId() {
  if (!isLoggedIn()) {
    // This ensure ensures that an anonymous user, that was previously logged in, doesn't get their
    // previous session's gtm_u_info cookie.
    return null;
  }
  let gtmCookie = cookies.get("gtm_u_info");
  if (gtmCookie === undefined) {
    return null;
  }
  gtmCookie = decodeURI(gtmCookie);
  // When a null or undefined value is passed through decodeURI, the return value will be a string
  // "null" or "undefined"
  if (gtmCookie === "null" || gtmCookie === "undefined") {
    return null;
  }
  gtmCookie = gtmCookie.split("|")[0];
  return !gtmCookie.length ? null : gtmCookie;
}

/**
 * Client-side encrypted hashId, to share with third-parties.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/digest
 * @param {String} [hashId] - decrypted hashId to be encrypted
 * @returns {Promise<String|null>} the encrypted of hashed user id or null
 * @example
 * getUserHashIdEncrypted("a864f96d5d44389c683b51f4c0c06d99");
 * // => "1e9f86e3743dabbbef58f645331617f4d1db8f534821cccdb6da61baa782fb3a"
 */
export function getUserHashIdEncrypted(hashId = getUserHashId()) {
  if (isNil(hashId)) {
    return Promise.resolve(null);
  }
  try {
    const msgUint8 = new TextEncoder().encode(hashId); // encode as (utf-8) Uint8Array
    return crypto.subtle.digest("SHA-256", msgUint8).then(hashBuffer => {
      const hashArray = Array.from(new Uint8Array(hashBuffer)); // convert buffer to byte array
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, "0")).join(""); // convert bytes to hex string
      return hashHex;
    });
  } catch (e) {
    // Browser doesn't support above methods.
    const fallback = hashId.split("").reverse().join("");
    return Promise.resolve(fallback);
  }
}

/**
 * @method getUserSophiId
 * @description Returns the Sophi 2 ID from the "sp_tgamid.a05e" cookie, or null
 * The value of the cookie does not change when you log out then log in as a different user
 * (because Data Science assumes that it's still the same person using the computer)
 * See: https://confluence.theglobeandmail.com/display/ARC/User+IDs
 * Related: Sophi 3 ID is from the "sophiTrackerid.a05e" cookie.
 * @returns {String|null} the Sophi 2 ID or null
 */
export function getUserSophiId() {
  let c = cookies.get("sp_tgamid.a05e");
  if (!c) {
    return null;
  }
  c = c.split(".")[0];
  return !c.length ? null : c;
}

/**
 * @method getUserRegion
 * @description Based on the user's identity, infer the user's province otherwise return an empty String
 * @see {@link https://www150.statcan.gc.ca/n1/pub/92-195-x/2011001/other-autre/pc-cp/tbl/tbl9-eng.htm|First character of the postal code and corresponding province, territory or region}
 * @param {Object} identity - the user object
 * @returns {String} of province short code or empty String if it doesn't find a match
 */
export function getUserRegionByPostal(identity) {
  let postal = getProp(identity, "postal", "");
  const firstChar = postal.substr(0, 1).toUpperCase();

  switch (firstChar) {
    case "A":
      return "NL";
    case "B":
      return "NS";
    case "C":
      return "PE";
    case "E":
      return "NB";
    case "G":
    case "H":
    case "J":
      return "QC";
    case "K": // Ottawa
    case "L":
    case "M":
    case "N":
    case "P":
      return "ON";
    case "R":
      return "MB";
    case "S":
      return "SK";
    case "T":
      return "AB";
    case "V":
      return "BC";
    case "X":
      return "NT";
    case "Y":
      return "YT";
    default:
      return "";
  }
}

/**
 * Generates comma-separated list of user entitlements to display in system box
 * @returns {String} of entitlements
 */
export function getUserEntitlements() {
  const user = {
    subscribed: {
      currentUser: isSubscribed(),
      serviceString: "PREMIUM"
    },
    ga: {
      currentUser: isGlobeAdvisor(),
      serviceString: "GA"
    },
    globePrint: {
      currentUser: isGlobePrintSubscriber(),
      serviceString: "NPGAM"
    },
    timesPrint: {
      currentUser: isNewYorkTimesPrintSubscriber(),
      serviceString: "NYT"
    },
    finTrial: {
      currentUser: isFinancialToolsTrial(),
      serviceString: "FINTRIAL"
    },
    anonymous: {
      currentUser: !isLoggedIn(),
      serviceString: "N/A"
    },
    registered: {
      currentUser: isRegistered(),
      serviceString: "NULL"
    }
  };
  return Object.values(user)
    .filter(entitlement => entitlement.currentUser)
    .map(entitlement => entitlement.serviceString)
    .join(", ");
}

/**
 * Gets Globe Advisor type
 * @returns {String} of GA type
 */
export function getGlobeAdvisorType() {
  return getProp(tgamConfig, "datalayer.identity.userAttributes.GA_SEGMENT", "");
}
